import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  ProviderCapability,
  PublicAnalyticsHealthEntry,
  PublicAnalyticsMetric,
  PublicAnalyticsSummary,
  PublicProviderReportPage,
  AnalyticsAvailability,
} from "../../../shared/types";

// ── Brand Analytics hooks (PRD-0118 Parent 7) ──
//
// These read the merged Parent 5/6 analytics API mounted at
// /api/brands/:brandId/analytics. The data layer is provider-neutral; the
// dashboard selects a capability and the backend returns reviewed,
// assignment-scoped facts for the provider-specific report contract.
//
// Dates are calendar dates (YYYY-MM-DD). The backend clamps windows to the
// assignment validity start and reaches earlier dates only via explicit
// backfill, so the UI never implies access to data that was never collected.

export interface AnalyticsDrilldownRow {
  dimensions: Record<string, string>;
  period_start: string;
  period_end: string;
  metrics: PublicAnalyticsMetric[];
}

interface DrilldownResponse {
  rows: AnalyticsDrilldownRow[];
  availability: AnalyticsAvailability;
}

export type GscReportKey = "gsc.daily" | "gsc.top_queries" | "gsc.top_pages" | "gsc.query_to_page";
export type Ga4ReportKey =
  | "ga4.daily"
  | "ga4.top_sources"
  | "ga4.top_landing_pages"
  | "ga4.top_channels"
  | "ga4.top_search_terms"
  | "ga4.page_events"
  | "ga4.internal_navigation";
export type YoutubeReportKey = "youtube.channel_daily";
export type FacebookPageReportKey = "facebook.page_daily";
export type InstagramReportKey = "instagram.professional_daily";
export type AnalyticsReportKey = GscReportKey | Ga4ReportKey | YoutubeReportKey | FacebookPageReportKey | InstagramReportKey;

export interface PublicDataViewVariant {
  id: string;
  template_id: string;
  template_version: number;
  title: string;
  status: "draft" | "approved";
  blocks: import("../../../shared/data-view-templates").DataViewBlock[];
  intent: string | null;
  approved_at: string | null;
  created_at: string;
}

/** Saved variants change only presentation; this read never starts collection. */
export function useDataViewVariants(brandId: string | undefined) {
  return useQuery({ queryKey: ["data-view-variants", brandId ?? null], queryFn: () => api.get<{ data: PublicDataViewVariant[] }>(`/api/brands/${encodeURIComponent(brandId!)}/data-view-variants`), enabled: !!brandId });
}

/** The agent may only propose a draft. Approval and data collection remain separate user actions. */
export function useGenerateDataViewVariant(brandId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { templateId: string; intent: string }) => api.post<{ data: PublicDataViewVariant }>(`/api/brands/${encodeURIComponent(brandId!)}/data-view-variants/generate`, { template_id: input.templateId, intent: input.intent }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["data-view-variants", brandId ?? null] }),
  });
}

export function useApproveDataViewVariant(brandId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) => api.post<{ data: PublicDataViewVariant }>(`/api/brands/${encodeURIComponent(brandId!)}/data-view-variants/${encodeURIComponent(variantId)}/approve`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["data-view-variants", brandId ?? null] }),
  });
}

/** Public response from the bounded, user-confirmed explicit collection action. */
export interface PublicAnalyticsCollectionOutcome {
  config_id: string;
  assignment_id: string;
  status: "skipped" | "already_running" | "succeeded" | "partial" | "failed";
  reason: string | null;
  run_id: string | null;
  rows: number;
  source_facts_written: number;
  facts_written: number;
  requested_start: string | null;
  requested_end: string | null;
  error_class: string | null;
  error_code: string | null;
}

export interface LiveGscSource {
  connectionId: string;
  resourceId: string;
  assignmentId: string;
}

/** Read Search Console directly for the demo; this does not ingest analytics facts. */
export function useLiveGscReports(
  brandId: string | undefined,
  source: LiveGscSource | null,
  startDate: string,
  endDate: string,
) {
  const reportKeys = ["gsc.daily", "gsc.top_queries", "gsc.top_pages"] as const;
  const queries = useQueries({
    queries: reportKeys.map((reportKey) => ({
      queryKey: ["live-gsc-report", brandId ?? null, source?.assignmentId ?? null, reportKey, startDate, endDate],
      queryFn: () => api.post<{ data: PublicProviderReportPage }>(
        `/api/connections/${encodeURIComponent(source!.connectionId)}/resources/${encodeURIComponent(source!.resourceId)}/assignments/${encodeURIComponent(source!.assignmentId)}/reports`,
        {
          brand_id: brandId,
          capability: "google.search_console.read",
          report_key: reportKey,
          start_date: startDate,
          end_date: endDate,
          row_limit: reportKey === "gsc.daily" ? 100 : 1000,
          data_state: "final",
          backfill: true,
        },
      ),
      enabled: !!brandId && !!source,
      staleTime: 5 * 60 * 1000,
    })),
  });
  return {
    daily: queries[0]?.data?.data.rows ?? [],
    queries: queries[1]?.data?.data.rows ?? [],
    pages: queries[2]?.data?.data.rows ?? [],
    loading: queries.some((query) => query.isLoading),
    error: queries.find((query) => query.error)?.error ?? null,
  };
}

export interface AnalyticsReadScope {
  /** Opaque public surface ID; the API enforces brand ownership. */
  surfaceId?: string | null;
  /** Opaque assignment public ID; never use capability aggregation with this. */
  assignmentId?: string | null;
}

export interface PublicAnalyticsPropertySource {
  assignment_id: string;
  capability: ProviderCapability;
  resource_id: string;
  resource_display_name: string | null;
  relationship: "native" | "referrer-by-assignment";
  compatibility: "compatible" | "mismatched" | "unverified";
  availability: AnalyticsAvailability;
  last_sync: string | null;
  headline_kpi: number | null;
}

export interface PublicAnalyticsProperty {
  surface_id: string;
  display_name: string;
  canonical_url: string | null;
  surface_kind?: "website" | "youtube" | "facebook";
  connected: boolean;
  sources: PublicAnalyticsPropertySource[];
  health: AnalyticsAvailability | "unconnected";
}

function calendarDate(d: Date): string {
  // Local YYYY-MM-DD — avoid UTC shifting the day.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Last N days as [start, end] calendar dates, end = today. */
export function lastNDays(n: number): [string, string] {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (n - 1));
  return [calendarDate(start), calendarDate(end)];
}

/** Brand analytics totals for a window (clicks, impressions, ctr, position for GSC). */
export function useBrandAnalyticsSummary(
  brandId: string | undefined,
  startDate: string,
  endDate: string,
  capability?: ProviderCapability | null,
  scope: AnalyticsReadScope = {},
  enabled = true,
) {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  if (capability) params.set("capability", capability);
  if (scope.surfaceId) params.set("surface", scope.surfaceId);
  if (scope.assignmentId) params.set("assignment", scope.assignmentId);
  return useQuery({
    queryKey: ["brand-analytics-summary", brandId ?? null, startDate, endDate, capability ?? null, scope.surfaceId ?? null, scope.assignmentId ?? null],
    queryFn: () =>
      api.get<{ data: PublicAnalyticsSummary }>(
        `/api/brands/${encodeURIComponent(brandId!)}/analytics/summary?${params.toString()}`,
      ),
    enabled: !!brandId && enabled,
    // GSC data has ~2-day native latency; a 5-min refetch catches scheduled syncs without hammering.
    refetchInterval: 5 * 60 * 1000,
  });
}

/** Dimensioned drill-down (top queries, top pages, daily series) for a report key. */
export function useBrandAnalyticsDrilldown(
  brandId: string | undefined,
  reportKey: AnalyticsReportKey,
  startDate: string,
  endDate: string,
  capability?: ProviderCapability | null,
  scope: AnalyticsReadScope = {},
  enabled = true,
) {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    report_key: reportKey,
  });
  if (capability) params.set("capability", capability);
  if (scope.surfaceId) params.set("surface", scope.surfaceId);
  if (scope.assignmentId) params.set("assignment", scope.assignmentId);
  return useQuery({
    queryKey: ["brand-analytics-drilldown", brandId ?? null, reportKey, startDate, endDate, capability ?? null, scope.surfaceId ?? null, scope.assignmentId ?? null],
    queryFn: () =>
      api.get<{ data: DrilldownResponse }>(
        `/api/brands/${encodeURIComponent(brandId!)}/analytics/drilldown?${params.toString()}`,
      ),
    enabled: !!brandId && enabled,
    refetchInterval: 5 * 60 * 1000,
  });
}

/** Surface-led property index. It deliberately includes unconnected website surfaces. */
export function useBrandAnalyticsProperties(brandId: string | undefined, startDate: string, endDate: string) {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  return useQuery({
    queryKey: ["brand-analytics-properties", brandId ?? null, startDate, endDate],
    queryFn: () => api.get<{ data: PublicAnalyticsProperty[] }>(`/api/brands/${encodeURIComponent(brandId!)}/analytics/properties?${params}`),
    enabled: !!brandId,
    refetchInterval: 5 * 60 * 1000,
  });
}

/** Fetch only the current property; do not fetch the whole index to render a detail page. */
export function useBrandAnalyticsProperty(brandId: string | undefined, surfaceId: string | undefined, startDate: string, endDate: string) {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  return useQuery({
    queryKey: ["brand-analytics-property", brandId ?? null, surfaceId ?? null, startDate, endDate],
    queryFn: () => api.get<{ data: PublicAnalyticsProperty }>(`/api/brands/${encodeURIComponent(brandId!)}/analytics/properties/${encodeURIComponent(surfaceId!)}?${params}`),
    enabled: !!brandId && !!surfaceId,
    refetchInterval: 5 * 60 * 1000,
  });
}

/** Fetch top_sources independently for every GA4 assignment on a property. */
export function useGa4ReferrerDrilldowns(
  brandId: string | undefined,
  surfaceId: string | undefined,
  assignmentIds: readonly string[],
  startDate: string,
  endDate: string,
) {
  return useQueries({
    queries: assignmentIds.map((assignmentId) => {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate, report_key: "ga4.top_sources", capability: "google.analytics.read", surface: surfaceId ?? "", assignment: assignmentId });
      return {
        queryKey: ["brand-analytics-referrers", brandId ?? null, surfaceId ?? null, assignmentId, startDate, endDate],
        queryFn: () => api.get<{ data: DrilldownResponse }>(`/api/brands/${encodeURIComponent(brandId!)}/analytics/drilldown?${params}`),
        enabled: !!brandId && !!surfaceId && !!assignmentId,
        refetchInterval: 5 * 60 * 1000,
      };
    }),
  });
}

/** Per-assignment health: connection, capability, resource, sync status, freshness, finality. */
export function useBrandAnalyticsHealth(brandId: string | undefined) {
  return useQuery({
    queryKey: ["brand-analytics-health", brandId ?? null],
    queryFn: () =>
      api.get<{ data: PublicAnalyticsHealthEntry[] }>(
        `/api/brands/${encodeURIComponent(brandId!)}/analytics/health`,
      ),
    enabled: !!brandId,
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Explicit Data Views are intentionally the only frontend path that may invoke
 * this endpoint. Rendering a view remains a read-only GET; the mutation occurs
 * only after the marketer reviews and confirms the selected source and range.
 */
export function useCollectExplicitAnalytics(brandId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      assignmentId: string;
      reportKey: "ga4.daily" | "ga4.top_landing_pages" | "ga4.page_events" | "ga4.internal_navigation" | "ga4.top_sources";
      startDate: string;
      endDate: string;
    }) => api.post<{ data: PublicAnalyticsCollectionOutcome }>(
      `/api/brands/${encodeURIComponent(brandId!)}/analytics/collect`,
      {
        assignment_id: input.assignmentId,
        report_key: input.reportKey,
        start_date: input.startDate,
        end_date: input.endDate,
      },
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["brand-analytics-drilldown", brandId ?? null] });
      await queryClient.invalidateQueries({ queryKey: ["brand-analytics-summary", brandId ?? null] });
    },
  });
}

// ── GSC metric helpers ──
//
// GSC canonical metrics (from the metric registry): search.clicks, search.impressions,
// search.ctr (ratio, recomputed clicks/impressions), and search.average_position
// (impression-weighted). The registry canonicalizes gsc.position -> search.average_position,
// so the hook must read that key — NOT "search.position" (which the backend never emits).

const GSC_METRIC_KEYS = {
  clicks: "search.clicks",
  impressions: "search.impressions",
  ctr: "search.ctr",
  position: "search.average_position",
} as const;

// ── GA4 metric helpers ──
//
// GA4 canonical metrics (from the metric registry): analytics.sessions,
// analytics.engaged_sessions, analytics.page_views, analytics.key_events,
// analytics.active_users. engagement_rate is intentionally source-only
// (ga4.engagement_rate) — no cross-provider engagement canonical exists — and is
// recomputed by the backend as engaged_sessions / sessions.

const GA4_METRIC_KEYS = {
  sessions: "analytics.sessions",
  engagedSessions: "analytics.engaged_sessions",
  engagementRate: "ga4.engagement_rate",
  pageViews: "analytics.page_views",
  keyEvents: "analytics.key_events",
  activeUsers: "analytics.active_users",
} as const;

function findMetric(metrics: PublicAnalyticsMetric[], key: string): PublicAnalyticsMetric | undefined {
  return metrics.find((metric) => metric.metric === key);
}

export interface GscTotals {
  clicks: number;
  impressions: number;
  ctr: number; // 0–1 ratio
  position: number; // average rank
  availability: AnalyticsAvailability;
  finality: string | null;
  freshThrough: string | null;
}

/** Project a PublicAnalyticsSummary into the four GSC totals the dashboard shows. */
export function gscTotalsFromSummary(summary: PublicAnalyticsSummary | undefined): GscTotals | null {
  if (!summary) return null;
  const clicks = findMetric(summary.metrics, GSC_METRIC_KEYS.clicks);
  const impressions = findMetric(summary.metrics, GSC_METRIC_KEYS.impressions);
  const ctr = findMetric(summary.metrics, GSC_METRIC_KEYS.ctr);
  const position = findMetric(summary.metrics, GSC_METRIC_KEYS.position);
  return {
    clicks: clicks?.value ?? 0,
    impressions: impressions?.value ?? 0,
    ctr: ctr?.value ?? 0,
    position: position?.value ?? 0,
    availability: summary.availability,
    finality: summary.finality,
    freshThrough: summary.fresh_through,
  };
}

export interface Ga4Totals {
  sessions: number;
  engagedSessions: number;
  engagementRate: number; // 0–1 ratio, recomputed engaged/sessions
  pageViews: number;
  keyEvents: number;
  /** null when the summary can't compute a range total (active_users is non-additive; provider_defined aggregation returns null for multi-day ranges). */
  activeUsers: number | null;
  availability: AnalyticsAvailability;
  finality: string | null;
  freshThrough: string | null;
}

/** Project a PublicAnalyticsSummary into the GA4 onsite-traffic totals. */
export function ga4TotalsFromSummary(summary: PublicAnalyticsSummary | undefined): Ga4Totals | null {
  if (!summary) return null;
  return {
    sessions: findMetric(summary.metrics, GA4_METRIC_KEYS.sessions)?.value ?? 0,
    engagedSessions: findMetric(summary.metrics, GA4_METRIC_KEYS.engagedSessions)?.value ?? 0,
    engagementRate: findMetric(summary.metrics, GA4_METRIC_KEYS.engagementRate)?.value ?? 0,
    pageViews: findMetric(summary.metrics, GA4_METRIC_KEYS.pageViews)?.value ?? 0,
    keyEvents: findMetric(summary.metrics, GA4_METRIC_KEYS.keyEvents)?.value ?? 0,
    // active_users is provider_defined (non-additive across days): aggregateFacts returns null for multi-day ranges. Propagate null (don't coerce to 0) so the dashboard renders '—' (no data / unsupported aggregation) instead of a misleading '0'.
    activeUsers: findMetric(summary.metrics, GA4_METRIC_KEYS.activeUsers)?.value ?? null,
    availability: summary.availability,
    finality: summary.finality,
    freshThrough: summary.fresh_through,
  };
}

/** Find a metric value on a drill-down row by canonical key. */
export function rowMetricValue(row: AnalyticsDrilldownRow, key: keyof typeof GSC_METRIC_KEYS): number {
  const metric = findMetric(row.metrics, GSC_METRIC_KEYS[key]);
  return metric?.value ?? 0;
}

/** Find a GA4 metric value on a drill-down row by canonical key. Returns null when the metric is absent or the aggregation yields null (provider_defined metrics, e.g. active_users across a multi-day range). */
export function ga4RowMetricValue(row: AnalyticsDrilldownRow, key: keyof typeof GA4_METRIC_KEYS): number | null {
  const metric = findMetric(row.metrics, GA4_METRIC_KEYS[key]);
  return metric?.value ?? null;
}

/** Format a 0–1 CTR as a percentage string. */
export function formatCtr(ratio: number): string {
  if (!Number.isFinite(ratio)) return "—";
  return `${(ratio * 100).toFixed(1)}%`;
}

/** Format an average position (lower is better). */
export function formatPosition(value: number): string {
  if (!Number.isFinite(value) || value === 0) return "—";
  return value.toFixed(1);
}

/** Format an integer count with thousands separators. null renders as '—' (no data). */
export function formatCount(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US");
}

/** Format a 0–1 ratio (engagement rate) as a percentage. */
export function formatRatio(ratio: number): string {
  return formatCtr(ratio);
}
